import { useState, useEffect } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { TripParticipantsDialog } from "@/components/TripParticipantsDialog";
import { AppHeader } from "@/components/header/AppHeader";
import { TripManager } from "@/components/TripManager";
import { NewTripDialog } from "@/components/trip/NewTripDialog";
import { TripContent } from "@/components/trip/TripContent";
import { useToast } from "@/hooks/use-toast";
import { useTripManagement } from "@/hooks/useTripManagement";
import { TripDialog } from "@/components/trip/TripDialog";

const Index = () => {
  const { toast } = useToast();
  const {
    currentTripName,
    tripHistory,
    expenses,
    handleAddExpense,
    handleDeleteExpense,
    handleStartNewTrip,
    handleEndTrip
  } = useTripManagement();

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const [showParticipantsDialog, setShowParticipantsDialog] = useState(false);
  const [tripParticipants, setTripParticipants] = useState<string[]>(() => {
    const saved = localStorage.getItem('tripParticipants');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('tripParticipants', JSON.stringify(tripParticipants));
  }, [tripParticipants]);

  const handleSaveParticipants = (participants: string[]) => {
    setTripParticipants(participants);
    toast({
      title: "Participants Saved",
      description: `${participants.length} participants added to the trip.`,
    });
  };

  const handleExportCSV = () => {
    toast({
      title: "Export Successful",
      description: "Your expenses have been exported to CSV format.",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-1 md:py-8 relative overflow-hidden">
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-primary origin-left z-50"
        style={{ scaleX }}
      />
      
      <motion.div 
        className="absolute top-0 right-0 w-96 h-96 bg-gradient-secondary opacity-10 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 90, 0],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      
      <div className="container mx-auto px-2 md:px-4 flex flex-col min-h-screen max-w-full relative z-10">
        <AppHeader 
          onOpenParticipants={() => setShowParticipantsDialog(true)}
          onExportCSV={handleExportCSV}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex-grow"
        >
          {!currentTripName ? (
            <div className="mb-4 md:mb-6">
              <TripDialog />
            </div>
          ) : (
            <div className="mb-4 md:mb-6">
              <TripManager 
                expenses={expenses}
                tripName={currentTripName}
                onEndTrip={handleEndTrip}
              />
            </div>
          )}

          <TripContent 
            tripParticipants={tripParticipants}
            onAddExpense={handleAddExpense}
            onDeleteExpense={handleDeleteExpense}
          />
        </motion.div>

        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-2 md:mt-8 py-2 md:py-4 text-center text-xs md:text-sm text-gray-600 bg-gradient-glass backdrop-blur-sm rounded-lg"
        >
          <p className="font-medium">
            travelpocket developed by mohammed maaz
          </p>
        </motion.footer>
      </div>

      <TripParticipantsDialog
        open={showParticipantsDialog}
        onOpenChange={setShowParticipantsDialog}
        onSave={handleSaveParticipants}
        initialParticipants={tripParticipants}
      />
    </div>
  );
};

export default Index;
